// Dispatcher and provider loader. All public tracking calls flow through
// trackAnalyticsEvent, which gates on consent, looks up the event's provider
// routing in ANALYTICS_EVENT_CATALOG, and fans out to every provider listed.
// Adding a new provider means adding a case here and adding entries in the
// catalog — no call site changes.

import { getAnalyticsConsent } from '@/lib/tracking/consent';
import {
	ANALYTICS_EVENT_CATALOG,
	ANALYTICS_EVENT_PAGE_VIEW,
	AnalyticsEventName,
	AnalyticsEventPayload,
	AnalyticsProviderId,
} from '@/lib/tracking/analytics-events';
import {
	dispatchGoogle,
	loadGoogleAnalytics,
	loadGoogleTagManager,
} from '@/lib/tracking/providers/google';
import {
	dispatchMeta,
	loadMetaPixel,
} from '@/lib/tracking/providers/meta';


export interface LoadAnalyticsProvidersConfig {
	gaId?: string | null;
	gtmId?: string | null;
	metaPixelId?: string | null;
}

export const loadAnalyticsProviders = (config: LoadAnalyticsProvidersConfig): void => {
	if(!getAnalyticsConsent('analytics')) { return; }
	if(config.gaId) { loadGoogleAnalytics(config.gaId); }
	if(config.gtmId) { loadGoogleTagManager(config.gtmId); }
	if(config.metaPixelId) { loadMetaPixel(config.metaPixelId); }
};


const dispatchToProvider = (provider: AnalyticsProviderId, eventName: string, payload: AnalyticsEventPayload): void => {
	switch(provider) {
		case 'google':
			dispatchGoogle(eventName, payload);
			return;
		case 'meta':
			dispatchMeta(eventName, payload);
			return;
	}
};


export const trackAnalyticsEvent = (eventName: AnalyticsEventName, payload: AnalyticsEventPayload = {}): void => {

	if(!getAnalyticsConsent('analytics')) { return; }

	const entry = ANALYTICS_EVENT_CATALOG[eventName];
	if(!entry) {
		// Unknown event — silently no-op rather than throwing. Catalog is the
		// source of truth; if an event isn't there, no provider should fire.
		return;
	}

	(Object.keys(entry) as AnalyticsProviderId[]).forEach((providerId) => {
		const route = entry[providerId];
		if(!route) { return; }
		const providerPayload = route.map ? route.map(payload) : payload;
		try {
			dispatchToProvider(providerId, route.name, providerPayload);
		} catch(err) {
			// Never let a tracking failure break a user flow.
			console.error(`[tracking] dispatch failed for ${providerId}:${route.name}`, err);
		}
	});
};


export const trackPageView = (pathname: string | null | undefined): void => {
	if(!pathname) { return; }
	trackAnalyticsEvent(ANALYTICS_EVENT_PAGE_VIEW, { page_path: pathname });
};
